import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { AvailabilitySlot, DAY_LABELS, CAPACITY_COLORS } from '../availability-schedules.model';
import { groupByDay, slotCoversHour, countProvidersForDay } from './calendarUtils';
import ProviderSlot from './ProviderSlot';

interface CalendarGridProps {
  slots: AvailabilitySlot[];
}

// Hours displayed: 7 AM to 9 PM
const HOURS = Array.from({ length: 15 }, (_, i) => i + 7);

const formatHourLabel = (hour: number): string => {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
};

const getCellColor = (providerCount: number): string | undefined => {
  if (providerCount === 0) return undefined;
  if (providerCount === 1) return `${CAPACITY_COLORS[1]}18`; // amber with low opacity
  if (providerCount === 2) return `${CAPACITY_COLORS[2]}18`; // purple with low opacity
  return `${CAPACITY_COLORS[3]}18`; // green with low opacity
};

const CalendarGrid: React.FC<CalendarGridProps> = ({ slots }) => {
  const byDay = groupByDay(slots);

  return (
    <Paper variant="outlined" sx={{ overflow: 'auto' }}>
      {/* Header row: day labels + capacity */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '80px repeat(7, 1fr)',
          borderBottom: '2px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ p: 1, borderRight: '1px solid', borderColor: 'divider' }} />
        {DAY_LABELS.map((label, dow) => {
          const daySlots = byDay[dow] || [];
          const providerCount = countProvidersForDay(daySlots);
          return (
            <Box
              key={dow}
              sx={{
                p: 1,
                textAlign: 'center',
                borderRight: dow < 6 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {label}
              </Typography>
              <Chip
                label={
                  providerCount > 0
                    ? `${providerCount} provider${providerCount > 1 ? 's' : ''}`
                    : '—'
                }
                size="small"
                variant={providerCount > 0 ? 'filled' : 'outlined'}
                sx={{
                  mt: 0.5,
                  fontSize: '0.7rem',
                  height: 20,
                  ...(providerCount > 0 && {
                    backgroundColor:
                      providerCount >= 3
                        ? CAPACITY_COLORS[3]
                        : providerCount === 2
                          ? CAPACITY_COLORS[2]
                          : CAPACITY_COLORS[1],
                    color: '#fff',
                  }),
                }}
              />
            </Box>
          );
        })}
      </Box>

      {/* Hour rows */}
      {HOURS.map((hour) => (
        <Box
          key={hour}
          sx={{
            display: 'grid',
            gridTemplateColumns: '80px repeat(7, 1fr)',
            minHeight: 48,
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          {/* Time label */}
          <Box
            sx={{
              p: 0.5,
              pr: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              borderRight: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {formatHourLabel(hour)}
            </Typography>
          </Box>

          {/* Day cells */}
          {Array.from({ length: 7 }, (_, dow) => {
            const daySlots = byDay[dow] || [];
            const slotsInHour = daySlots.filter((s) => slotCoversHour(s, hour));
            const bgColor = getCellColor(slotsInHour.length);

            return (
              <Box
                key={dow}
                sx={{
                  borderRight: dow < 6 ? '1px solid' : 'none',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  p: 0.25,
                  backgroundColor: bgColor,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignContent: 'flex-start',
                }}
              >
                {slotsInHour.map((slot) => (
                  <ProviderSlot key={slot.id} slot={slot} />
                ))}
              </Box>
            );
          })}
        </Box>
      ))}
    </Paper>
  );
};

export default CalendarGrid;
